import { api } from '@core/api';
import type { CreateReservationResponse, DepositGrabPayload, DepositGrabMercadoPagoPayload } from './reservations';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WlAlternativesPayload {
  dates: string[];
  restId: number;
  serviceType: string;
  pax: number;
  menus: number[];
}

export interface WlAlternativeOption {
  date: string;
  serviceType: string;
  hours: string[];
}

export interface WlGuaranteeRulesPayload {
  dates: string[];
  restId: number;
  serviceType: string;
  pax: number;
  menus: number[];
  hour: string;
}

export interface WlGuaranteeRulesResponse {
  type: 'traditional' | 'deposit' | 'no_show';
  fee: number;
  feeUSD: number;
}

/**
 * WL traditional payload — same as traditional but date is an array.
 */
export interface WlTraditionalPayload {
  restaurant: number;
  date: string[];
  time: string;
  partySize: number;
  menu: number;
  area: number;
  type: 'traditional';
  source: string;
  pairings: unknown[];
  veggie: boolean;
  howManyVeggie: number;
  kids: boolean;
  howManyKids: number;
  allergies: string;
  pets: boolean;
  accesibility: boolean;
  fee: number;
  gratuity: number;
  giftCardToken: string | null;
  suggestedFromWaitingListAlternatives: boolean;
  agentReservation?: boolean;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  requestGuarantee?: boolean;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetch alternative availability for the selected WL dates.
 * POST /api/waiting-list-alternatives
 */
export async function fetchWaitingListAlternatives(
  payload: WlAlternativesPayload,
): Promise<WlAlternativeOption[]> {
  return api.post<WlAlternativeOption[]>(
    '/api/waiting-list-alternatives',
    payload,
  );
}

/**
 * Fetch guarantee rules for a WL reservation.
 * POST /api/waiting-list-guarantee-rules
 */
export async function fetchWaitingListGuaranteeRules(
  payload: WlGuaranteeRulesPayload,
): Promise<WlGuaranteeRulesResponse> {
  return api.post<WlGuaranteeRulesResponse>(
    '/api/waiting-list-guarantee-rules',
    payload,
  );
}

/**
 * Create a traditional WL reservation (no guarantee).
 * POST /api/{lang}/traditional-waiting-list
 */
export async function createWlTraditionalReservation(
  lang: string,
  payload: WlTraditionalPayload,
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/traditional-waiting-list`,
    { data: payload },
  );
}

/**
 * Create a WL reservation with Stripe guarantee.
 * POST /api/{lang}/deposit-waiting-list-stripe
 */
export async function createWlDepositStripe(
  lang: string,
  payload: DepositGrabPayload & { date: string[] },
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/deposit-waiting-list-stripe`,
    { data: payload },
  );
}

/**
 * Create a WL reservation with MercadoPago guarantee.
 * POST /api/{lang}/deposit-waiting-list-mercadopago
 */
export async function createWlDepositMercadoPago(
  lang: string,
  payload: DepositGrabMercadoPagoPayload & { date: string[] },
): Promise<CreateReservationResponse> {
  return api.post<CreateReservationResponse>(
    `/api/${lang}/deposit-waiting-list-mercadopago`,
    { data: payload },
  );
}

